import { Expandable, Text } from '@components/ui';
import {
  AssignmentOutlined,
  ManageSearch,
  PersonOutline,
  VerifiedUser,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export const CAPABILITY = {
  VALIDATOR: 'validator',
  INSPECTOR: 'inspector',
} as const;

type CapabilityKey = (typeof CAPABILITY)[keyof typeof CAPABILITY];

interface CapabilityOption {
  key: CapabilityKey;
  label: string;
  icon: typeof VerifiedUser;
}

const CAPABILITY_OPTIONS: CapabilityOption[] = [
  {
    key: CAPABILITY.VALIDATOR,
    label: 'auth.validator',
    icon: VerifiedUser,
  },
  {
    key: CAPABILITY.INSPECTOR,
    label: 'auth.inspector',
    icon: ManageSearch,
  },
];

const CAPABILITY_COLOR: Record<CapabilityKey, 'success.main' | 'primary.main'> =
  {
    validator: 'success.main',
    inspector: 'primary.main',
  };

const roleTabSx = (active: boolean): SxProps<Theme> => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1,
  p: 2,
  cursor: 'pointer',
  borderRadius: '12px',
  border: '1px solid',
  borderColor: active ? 'primary.main' : 'transparent',
  bgcolor: active
    ? 'background.paper'
    : (theme) => (theme.palette.mode === 'dark' ? '#262a2e' : '#f7f8fa'),
  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
  color: 'text.primary',
  font: 'inherit',
  transition: 'all 0.2s',
});

interface RoleTabProps {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  title: string;
  subtitle: string;
}

const RoleTab = ({
  active,
  onClick,
  icon,
  title,
  subtitle,
}: RoleTabProps): JSX.Element => (
  <Grid item xs={6}>
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={roleTabSx(active)}
    >
      {icon}
      <Text as="span" variant="label" sx={{ fontWeight: 700 }}>
        {title}
      </Text>
      <Text as="span" variant="caption" tone="muted">
        {subtitle}
      </Text>
    </Box>
  </Grid>
);

interface SignupRoleFieldsProps {
  userRole: 'buyer' | 'r8tr' | null;
  subAccount: boolean;
  capabilities: string[];
  onRoleChange: (role: 'buyer' | 'r8tr') => void;
  onCapabilityToggle: (capability: string) => void;
}

const SignupRoleFields = ({
  userRole,
  subAccount,
  capabilities,
  onRoleChange,
  onCapabilityToggle,
}: SignupRoleFieldsProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <>
      <Text
        variant="body"
        tone="muted"
        align="center"
        sx={{ mb: 2, fontSize: '0.875rem' }}
      >
        {t('auth.areYou')}
      </Text>
      {!subAccount && (
        <Grid container spacing={1.5} sx={{ mb: 3.5 }}>
          <RoleTab
            active={userRole === 'r8tr'}
            onClick={() => onRoleChange('r8tr')}
            icon={
              <AssignmentOutlined
                fontSize="small"
                sx={{
                  color:
                    userRole === 'r8tr' ? 'primary.main' : 'text.secondary',
                }}
              />
            }
            title={t('common.r8tr')}
            subtitle={t('auth.inspectors')}
          />
          <RoleTab
            active={userRole === 'buyer'}
            onClick={() => onRoleChange('buyer')}
            icon={
              <PersonOutline
                fontSize="small"
                sx={{
                  color:
                    userRole === 'buyer' ? 'primary.main' : 'text.secondary',
                }}
              />
            }
            title={t('common.buyer')}
            subtitle={t('auth.shoppers')}
          />
        </Grid>
      )}
      <Expandable open={userRole != null}>
        <Box sx={{ mb: 2, mt: 3 }}>
          <Text
            variant="body"
            tone="muted"
            align="center"
            sx={{ mb: 1.5, fontSize: '0.875rem' }}
          >
            {t('auth.yourCapabilities')}
          </Text>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {CAPABILITY_OPTIONS.map((option, optionIndex) => {
              const selected = capabilities.includes(option.key);
              const Icon = option.icon;
              return (
                <Box
                  key={option.key}
                  component="label"
                  style={
                    {
                      '--capability-delay': `${optionIndex * 90}ms`,
                    } as CSSProperties
                  }
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    border: '2px solid',
                    borderColor: selected
                      ? CAPABILITY_COLOR[option.key]
                      : 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: (theme) =>
                      selected
                        ? alpha(
                            theme.palette[
                              option.key === CAPABILITY.VALIDATOR
                                ? 'success'
                                : 'primary'
                            ].main,
                            0.08
                          )
                        : alpha(theme.palette.text.secondary, 0.12),
                    transition: 'all 0.2s ease',
                    animation: 'capability-card-in 0.4s ease-out both',
                    animationDelay: 'var(--capability-delay, 0ms)',
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                      '&::after': { animation: 'none' },
                    },
                    '&::after': {
                      content: '""',
                      pointerEvents: 'none',
                      position: 'absolute',
                      inset: 0,
                      transform: 'translateX(-120%)',
                      animation: 'capability-sheen 0.65s ease-out both',
                      animationDelay:
                        'calc(var(--capability-delay, 0ms) + 120ms)',
                      background:
                        'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.08) 35%, transparent 65%)',
                    },
                  }}
                >
                  <Checkbox
                    checked={selected}
                    onChange={() => onCapabilityToggle(option.key)}
                    size="small"
                    sx={{ p: 0.5, mr: 1.5 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon sx={{ fontSize: 16 }} />
                    <Text
                      as="span"
                      variant="label"
                      tone={selected ? 'default' : 'muted'}
                      sx={{
                        fontWeight: selected ? 600 : 400,
                        color: selected
                          ? CAPABILITY_COLOR[option.key]
                          : undefined,
                      }}
                    >
                      {t(option.label)}
                    </Text>
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Text
            variant="caption"
            tone="primary"
            sx={{
              mt: 1,
              display: 'block',
              minHeight: 16,
              visibility: capabilities.length === 0 ? 'visible' : 'hidden',
            }}
          >
            {t('auth.selectCapability')}
          </Text>
        </Box>
      </Expandable>
    </>
  );
};

export default SignupRoleFields;
